import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, TableIcon } from '@interfaces';
import { CustomerCrmService, PromptService, RmsMessageService, RmsPulldownService } from '@services';
import { ContactFormComponent } from './contact-form/contact-form.component';

@Component({
  selector: 'app-customer-contacts',
  imports: [RmsModule],
  templateUrl: './customer-contacts.component.html',
  styleUrl: './customer-contacts.component.scss'
})
export class CustomerContactsComponent implements OnChanges {
  @Input() companyId!: number;
  @Input() canEdit = false;
  Contacts: any[] = [];

  Columns: Column[] = [
    { label: 'Contact', key: 'FullName', width: 18 },
    { label: 'Roles', key: 'Roles', width: 18 },
    { label: 'Email', key: 'Email', width: 20 },
    { label: 'Phone', key: 'Phone', width: 12 },
    { label: 'Invoice', key: 'InvoiceText', width: 10 },
    { label: 'Confirmation', key: 'ConfirmationText', width: 12 },
    { label: 'PRO', key: 'ProText', width: 10 },
  ];

  constructor(
    private crm: CustomerCrmService,
    private prompt: PromptService,
    private message: RmsMessageService,
    private pulldown: RmsPulldownService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['companyId']) {
      this.load();
    }
  }

  load() {
    if (!this.companyId) return;
    this.crm.getContacts(this.companyId).subscribe({
      next: (contacts) => {
        this.Contacts = (contacts || []).map((c: any) => ({
          ...c,
          FullName: [c.FirstName, c.LastName].filter(Boolean).join(' ') || '—',
          InvoiceText: c.IsInvoiceRecipient ? 'Yes' : 'No',
          ConfirmationText: c.IsConfirmationRecipient ? 'Yes' : 'No',
          ProText: c.IsProNotificationRecipient ? 'Yes' : 'No'
        }));
      },
      error: (err) => this.message.error('Failed to load contacts: ' + this.errorText(err))
    });
  }

  getIcons = (row: any): TableIcon[] => {
    if (!this.canEdit) return [];
    const icons: TableIcon[] = [
      { icon: 'edit', title: 'Edit', action: () => this.openContactForm(row) }
    ];
    if (row.IsActive) {
      icons.push({ icon: 'person_off', title: 'Deactivate', action: () => this.confirmDeactivate(row) });
    }
    return icons;
  };

  openContactForm(contact?: any) {
    if (!this.canEdit) return;
    const ref = this.pulldown.open(ContactFormComponent, {
      title: contact ? 'Edit Contact' : 'Add Contact',
      width: 'clamp(480px, 50vw, 720px)',
      data: contact ? { ...contact } : { CompanyID: this.companyId, IsActive: true, RoleIDs: [] }
    });

    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      result.CompanyID = this.companyId;
      result.ModifiedBy = localStorage.getItem('GUID');
      this.crm.saveContact(result).subscribe({
        next: () => {
          this.message.success(`Contact ${contact ? 'updated' : 'added'} successfully`);
          this.load();
        },
        error: (err) => this.message.error('Failed to save contact: ' + this.errorText(err))
      });
    });
  }

  confirmDeactivate(contact: any) {
    this.prompt.open({
      promptText: `Deactivate contact "${contact.FullName}"? They will no longer appear as an active contact, but their history is preserved.`,
      buttons: [
        {
          label: 'Deactivate', buttonClass: 'btn-bg-red', action: () => {
            this.prompt.close();
            this.crm.deactivateContact(contact.CustomerContactID, localStorage.getItem('GUID')).subscribe({
              next: () => {
                this.message.success('Contact deactivated');
                this.load();
              },
              error: (err) => this.message.error('Failed to deactivate contact: ' + this.errorText(err))
            });
          }
        }
      ]
    });
  }

  private errorText(err: any): string {
    return err?.error?.error?.message || err?.message || 'Unknown error';
  }
}
